import type { AxiosError } from 'axios';
import React, { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from 'zustand';

import { useFetchOrganizationOfCurrentUser } from '../api/hooks/auth';
import { useFetchAgreements, useFetchPatient, useFetchPatients } from '../api/hooks/patient';
import { useMinddSnackbar } from '../components/MinddSnackbarProvider';
import { FeedEvent } from '../socket';
import {
  createPatientStore,
  PatientState,
  PatientStoreApi,
  selectUser,
  selectUsers,
  selectTempPatients,
  selectUserAgreements,
  selectIsStimHomeAgreed,
  selectIsExpiredCommonAgree,
  selectIsStimAgreed,
  selectIsTempUser,
} from '../stores/createPatientStore';

import { useFeedSocketClient } from './FeedSocketProvider';

const TEMP_STORAGE_KEY = 'TEMP_USERS';

const PatientStoreContext = createContext<PatientStoreApi | null>(null);
const PatientGoneContext = createContext<(patientId: string) => void>(() => undefined);

// 파생 셀렉터 재노출: 소비처는 usePatientStore(selectUser)처럼 필요한 슬라이스만 구독한다.
export {
  selectUser,
  selectUsers,
  selectTempPatients,
  selectUserAgreements,
  selectIsStimHomeAgreed,
  selectIsExpiredCommonAgree,
  selectIsStimAgreed,
  selectIsTempUser,
};

export function usePatientStore<U>(selector: (s: PatientState) => U): U {
  const api = useContext(PatientStoreContext);
  if (!api) {
    throw new Error('usePatientStore must be used within PatientProvider');
  }
  return useStore(api, selector);
}

export const usePatientGone = () => useContext(PatientGoneContext);

type PatientSignal = { id: number; patientId: string; editInfo?: boolean };

const PatientProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: organization } = useFetchOrganizationOfCurrentUser();
  const orgId = organization?.id;
  // orgId가 async로 resolve(undefined→실값)되면 store를 재생성한다. useMemo는 값 보존이 보장되지
  // 않으므로(React가 메모를 버릴 수 있음), 재생성이 필요한 파생 store는 useState + prev 비교 패턴으로 둔다.
  const [storeApi, setStoreApi] = useState(() => createPatientStore(orgId));
  const [prevOrgId, setPrevOrgId] = useState(orgId);
  if (orgId !== prevOrgId) {
    setPrevOrgId(orgId);
    setStoreApi(createPatientStore(orgId));
  }

  // cross-tab temp user 동기화 (구 생성자 내부 리스너 → cleanup 포함 effect로 이동)
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === TEMP_STORAGE_KEY) {
        storeApi.getState()._setTempUsersFromStorage(event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storeApi]);

  const feedClient = useFeedSocketClient();
  const [pendingSignals, setPendingSignals] = useState<PatientSignal[]>([]);
  const nextSignalId = useRef(0);
  const deletedIds = useRef(new Set<string>());
  const goneHandlerRef = useRef<(patientId: string) => void>(() => undefined);
  const signal = pendingSignals[0];

  useEffect(() => {
    if (!feedClient) return;
    const subId = feedClient.subscribe(
      ['USER_AGREED', 'PATIENT_UPDATED', 'PATIENT_DELETED', 'SURVEY_SESSION_CREATED', 'SURVEY_SESSION_UPDATED', 'SURVEY_SESSION_DELETED'],
      (event: FeedEvent) => {
        // 삭제 신호는 네트워크가 필요 없어 재조회 줄을 기다리지 않고 바로 처리한다
        if (event.type === 'PATIENT_DELETED') {
          goneHandlerRef.current(event.patientId);
          return;
        }
        const id = nextSignalId.current++;
        let next: PatientSignal;
        if (event.type === 'PATIENT_UPDATED' || event.type === 'SURVEY_SESSION_DELETED') {
          next = { id, patientId: event.patientId, editInfo: true };
        } else {
          next = { id, patientId: event.patientId };
        }
        setPendingSignals((queue) => [...queue, next]);
      },
    );
    return () => feedClient.unsubscribe(subId);
  }, [feedClient]);

  // 구 observer의 store.user 반응 읽기 대체: user 변경 시 재렌더 → agreements refetch
  const userId = useStore(storeApi, (s) => selectUser(s)?.id);

  const { refetch: refetchPatient } = useFetchPatient(signal?.patientId, false);
  const { data: agreements, refetch: refetchAgreements } = useFetchAgreements(userId);
  const { data: usersData, hasNextPage, fetchNextPage } = useFetchPatients('');

  const { add: addSnackbar } = useMinddSnackbar();
  const { pathname } = useLocation();
  const onPatientScreenRef = useRef(false);
  useEffect(() => {
    onPatientScreenRef.current = pathname === '/patient' || pathname.startsWith('/patient/');
  }, [pathname]);
  const onPatientGone = React.useCallback(
    (patientId: string) => {
      deletedIds.current.add(patientId);
      setPendingSignals((queue) => queue.filter((s) => s.patientId !== patientId));
      const wasSelected = selectUser(storeApi.getState())?.id === patientId;
      storeApi.getState().deleteUser(patientId);
      if (wasSelected && onPatientScreenRef.current) {
        addSnackbar('이미 삭제된 환자입니다.');
      }
    },
    [storeApi, addSnackbar],
  );

  useEffect(() => {
    goneHandlerRef.current = onPatientGone;
  });

  const queueRef = useRef(pendingSignals);
  useEffect(() => {
    queueRef.current = pendingSignals;
  });

  useEffect(() => {
    if (!signal) return;
    // 이번 재조회가 같은 환자의 쌓인 신호를 모두 덮는다. 제자리 교체만 하는 갈래는 목록 삽입·맨 위 올리기를
    // 하지 않으므로, 합친 신호가 모두 정보 수정일 때만 그 갈래로 간다.
    const covered = queueRef.current.filter((s) => s.patientId === signal.patientId);
    const editInfo = covered.every((s) => s.editInfo);
    const process = async () => {
      if (signal.patientId === selectUser(storeApi.getState())?.id) {
        refetchAgreements();
      }
      const { data: patient, error } = await refetchPatient();
      if (error) {
        // react-query 는 오류 때도 직전 성공 data 를 남겨 삭제된 환자를 되살린다
        if ((error as AxiosError).response?.status === 404) {
          onPatientGone(signal.patientId);
        }
        return;
      }
      if (patient && !deletedIds.current.has(signal.patientId)) {
        storeApi.getState().updateUser(patient, editInfo);
      }
    };
    process().finally(() => {
      const handled = new Set(covered.map((s) => s.id));
      setPendingSignals((queue) => queue.filter((s) => !handled.has(s.id)));
    });
  }, [signal, storeApi, refetchPatient, refetchAgreements, onPatientGone]);

  useEffect(() => {
    if (agreements) {
      storeApi.getState().setUserAgreements(agreements.patientId, agreements?.agreements);
    }
  }, [agreements, storeApi]);

  useEffect(() => {
    const users = usersData?.pages || [];
    storeApi.getState().setUsers(users);
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [usersData, storeApi, hasNextPage, fetchNextPage]);

  return (
    <PatientStoreContext.Provider value={storeApi}>
      <PatientGoneContext.Provider value={onPatientGone}>{children}</PatientGoneContext.Provider>
    </PatientStoreContext.Provider>
  );
};

export default PatientProvider;
